import { useQuery } from "react-query";
import { ThumbnailSize } from "../../../components/Thumbnail";
import { getThumbnail } from "../../../queries/files";
import { useEffect, useRef } from "react";
import networkSelectors from "../../../selectors/network";
import { useSelector } from "react-redux";

type ThumbnailStatus = "Pending" | "Running" | "Complete" | "Error";

interface ThumbnailResponse {
  url: string | null;
  fileType: string | null;
  data?: {
    status: ThumbnailStatus;
    [key: string]: any;
  };
}

export const useThumbnail = (id: string, size: ThumbnailSize, interval: number = 2000, enabled: boolean = true) => {
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const refetchIntervalRef = useRef<number | null>(null);

  // TODO: Can be greatly simplified after upgrading to react-query v3+ with refetchInterval function
  const query = useQuery<ThumbnailResponse>(
    ["thumbnail", id, size],
    async () => {
      if (!id || !enabled) return { url: null, fileType: null, data: undefined };
      return await getThumbnail(networkHeaders)(id, size);
    },
    {
      enabled: enabled,
      keepPreviousData: true,
      retry: 3,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  useEffect(() => {
    if (refetchIntervalRef.current) {
      clearInterval(refetchIntervalRef.current);
    }

    if (enabled && query.data?.data?.status !== "Complete" && query.data?.data?.status !== "Error") {
      refetchIntervalRef.current = window.setInterval(() => {
        query.refetch();
      }, interval);
    }

    return () => {
      if (refetchIntervalRef.current) {
        clearInterval(refetchIntervalRef.current);
      }
    };
  }, [enabled, interval, query.data?.data?.status]);

  return { data: query.data, isLoading: query.isLoading };
};
